import { useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsArrowRight, BsCheck2Square, BsChevronDown, BsChevronRight } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import SectionCard from '#globalComponents/Card/SectionCard';
import { useAuth } from '#globalContexts/AuthProvider';

import { useCompleteActivity } from '../../../models/activities/mutations';
import { useActivitiesList } from '../../../models/activities/queries';
import { useDealsList } from '../../../models/deals/queries';

import TodoItem from './TodoItem';

const PAGE_SIZE = 10;

const TodayTodosList = ({ onOpenCompany }) => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const complete = useCompleteActivity();
	const [pendingIds, setPendingIds] = useState(() => new Set());
	const [overdueExpanded, setOverdueExpanded] = useState(false);

	const { todayStart, todayEnd } = useMemo(() => {
		const now = new Date();
		const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
		return { todayStart: start.toISOString(), todayEnd: end.toISOString() };
	}, []);

	const baseFilters = {
		activityTypes: 'task',
		excludeAutomated: true,
		orderBy: 'dueAt',
		orderDir: 'asc',
		pageSize: PAGE_SIZE,
		userIds: user?.id,
	};

	const { data: todayData, isLoading: todayLoading } = useActivitiesList(
		{ ...baseFilters, dueAfter: todayStart, dueBefore: todayEnd },
		{ enabled: Boolean(user?.id) },
	);

	const { data: overdueData, isLoading: overdueLoading } = useActivitiesList(
		{ ...baseFilters, dueBefore: todayStart, status: 'planned' },
		{ enabled: Boolean(user?.id) },
	);

	const todayItems = useMemo(() => todayData?.activities ?? [], [todayData?.activities]);
	const overdueItems = useMemo(() => overdueData?.activities ?? [], [overdueData?.activities]);
	const overdueTotal = overdueData?.total ?? overdueItems.length;

	const allDealIds = useMemo(() => {
		const ids = new Set();
		for (const a of todayItems) if (a.dealId) ids.add(a.dealId);
		for (const a of overdueItems) if (a.dealId) ids.add(a.dealId);
		return [...ids];
	}, [todayItems, overdueItems]);

	const { data: dealsData } = useDealsList({ status: 'open', pageSize: 500 }, { enabled: allDealIds.length > 0 });
	const dealToCompany = useMemo(() => {
		const map = new Map();
		for (const d of dealsData?.deals ?? []) map.set(d.id, d.crmCompanyId);
		return map;
	}, [dealsData]);

	const handleComplete = (activity) => {
		setPendingIds((prev) => {
			const next = new Set(prev);
			next.add(activity.id);
			return next;
		});
		complete.mutate({ id: activity.id, dealId: activity.dealId });
	};

	const renderItem = (a) => {
		const companyId = a.crmCompanyId ?? dealToCompany.get(a.dealId);
		return (
			<TodoItem
				key={a.id}
				activity={a}
				companyId={companyId}
				onOpenCompany={onOpenCompany}
				onComplete={handleComplete}
				isPending={pendingIds.has(a.id)}
			/>
		);
	};

	const goToAllActivities = () => navigate('/crm/activities');

	return (
		<SectionCard
			title="ToDos heute"
			icon={BsCheck2Square}
			headerAction={
				<Button onClick={goToAllActivities} variant="outline" size="sm">
					Alle Aktivitäten
					<BsArrowRight className="w-3 h-3" />
				</Button>
			}
		>
			{todayLoading && <p className="text-bgBase-800 text-sm">ToDos werden geladen …</p>}

			{!todayLoading && todayItems.length === 0 && (
				<p className="text-sm text-bgBase-800">Keine ToDos für heute.</p>
			)}

			<div className="space-y-2">{todayItems.map(renderItem)}</div>

			<div className="mt-4 pt-3 border-t border-bgBase-500">
				<button
					type="button"
					onClick={() => setOverdueExpanded((v) => !v)}
					className="flex items-center gap-2 text-xs font-semibold text-text hover:text-primary-600"
				>
					{overdueExpanded ? <BsChevronDown className="w-3 h-3" /> : <BsChevronRight className="w-3 h-3" />}
					<span>Überfällig</span>
					{!overdueLoading && (
						<span className="text-[11px] font-normal text-bgBase-800">({overdueTotal})</span>
					)}
				</button>

				{overdueExpanded && (
					<div className="mt-3">
						{overdueLoading && <p className="text-bgBase-800 text-sm">Wird geladen …</p>}
						{!overdueLoading && overdueItems.length === 0 && (
							<p className="text-sm text-bgBase-800">Keine überfälligen ToDos.</p>
						)}
						<div className="space-y-2">{overdueItems.map(renderItem)}</div>
					</div>
				)}
			</div>
		</SectionCard>
	);
};

TodayTodosList.propTypes = {
	onOpenCompany: PropTypes.func,
};

export default TodayTodosList;
